import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, createVNode as _createVNode, createTextVNode as _createTextVNode, Fragment as _Fragment, vShow as _vShow, withKeys as _withKeys, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "clinic-calendar-widget" }
const _hoisted_2 = {
  key: 0,
  class: "w-connection",
  role: "status",
  "aria-live": "polite"
}
const _hoisted_3 = ["data-narrow", "data-condensed", "aria-busy", "inert"]
const _hoisted_4 = ["role"]

import {
  computed, onBeforeUnmount, onMounted, ref, watch,
} from 'vue';
import { useScheduleStore } from '@/store/calendar/scheduleModule';
import type { CalendarContext, CalendarRequest } from '@/store/calendar/types';
import CalendarHeader from './calendar/CalendarHeader.vue';
import CalendarScreen from './calendar/CalendarScreen.vue';
import BranchSchedule from './calendar/BranchSchedule.vue';
import CalendarFooter from './calendar/CalendarFooter.vue';
import RoomMenu from './calendar/RoomMenu.vue';
import BranchPicker from './calendar/BranchPicker.vue';
import './calendar/calendar.css';

type CalendarProps = {
  context: CalendarContext;
  request: CalendarRequest;
  refreshRevision?: number;
};

type CalendarEvents = {
  (event: 'event', data: { event: string; payload: Record<string, unknown> }): void;
};


export default /*@__PURE__*/_defineComponent({
  __name: 'CalendarWidget',
  props: {
    context: {},
    request: { type: Function },
    refreshRevision: {}
  },
  emits: ["event"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;
const emit = __emit;

const store = useScheduleStore();
const root = ref<HTMLElement | null>(null);
let observer: ResizeObserver | null = null;

const toastRole = computed(() => (store.toast?.ok === false ? 'alert' : 'status'));

const refresh = () => store.refresh();
const setContext = (context: CalendarContext) => store.setContext(context);

// Пока календарь загружается или сохраняет, действия игнорируются — кроме «Обновить»
// и выбора филиала (до него календарь ещё не загружен)
const guardBusy = (event: Event) => {
  const target = event.target as HTMLElement;
  if (!store.busy || store.branchRequired || target.closest('[data-action="refresh"]')) return;
  if (event.type === 'click' && !target.closest('button')) return;
  event.stopPropagation();
  event.preventDefault();
};

// Ширина решает раскладку: узкая — один день, средняя — сокращённые подписи
const updateWidth = () => {
  const width = root.value?.clientWidth || 0;
  if (!width || width === store.layoutWidth) return;
  store.layoutWidth = width;
  store.closeRoomMenu();
};

// Перерисовка меняет высоту корня; если сделать её прямо в колбэке наблюдателя,
// браузер ругается «ResizeObserver loop completed…». Откладываем на следующий кадр
let frameId = 0;
const scheduleWidthUpdate = () => {
  cancelAnimationFrame(frameId);
  frameId = requestAnimationFrame(updateWidth);
};

onMounted(() => {
  store.connect(
    props.request,
    (event, payload) => emit('event', { event, payload }),
  );
  if (root.value && typeof ResizeObserver !== 'undefined') {
    observer = new ResizeObserver(scheduleWidthUpdate);
    observer.observe(root.value);
  }
  store.setContext(props.context);
});

watch(() => props.context, setContext, { deep: true });
watch(() => props.refreshRevision, refresh);

onBeforeUnmount(() => {
  observer?.disconnect();
  cancelAnimationFrame(frameId);
  store.disconnect();
});

__expose({ refresh, setContext });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_unref(store).connection)
      ? (_openBlock(), _createElementBlock("section", _hoisted_2, [
          _createElementVNode("p", null, _toDisplayString(_unref(store).connection.message), 1),
          (_unref(store).connection.retry)
            ? (_openBlock(), _createElementBlock("button", {
                key: 0,
                type: "button",
                onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(store).retry()))
              }, " Повторить подключение "))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true),
    _withDirectives(_createElementVNode("div", {
      ref_key: "root",
      ref: root,
      class: "w-root",
      "data-narrow": String(_unref(store).narrow),
      "data-condensed": String(_unref(store).condensed),
      "aria-busy": String(_unref(store).loading || _unref(store).mutating),
      inert: _unref(store).mutating,
      onClickCapture: guardBusy,
      onChangeCapture: guardBusy,
      onKeydown: _cache[1] || (_cache[1] = _withKeys(($event: any) => (_unref(store).closeRoomMenu()), ["esc"]))
    }, [
      (_unref(store).branchRequired)
        ? (_openBlock(), _createBlock(BranchPicker, { key: 0 }))
        : (_unref(store).initialized)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
              _createVNode(CalendarHeader),
              (_unref(store).screen === 'branch')
                ? (_openBlock(), _createBlock(BranchSchedule, { key: 0 }))
                : (_openBlock(), _createBlock(CalendarScreen, { key: 1 })),
              _createElementVNode("div", {
                class: "w-toast",
                "aria-live": "polite",
                role: toastRole.value
              }, [
                (_unref(store).toast)
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                      _createTextVNode(_toDisplayString(_unref(store).toast.text), 1)
                    ], 64))
                  : _createCommentVNode("", true)
              ], 8, _hoisted_4),
              _createVNode(CalendarFooter),
              _createVNode(RoomMenu, { container: root.value }, null, 8, ["container"])
            ], 64))
          : _createCommentVNode("", true)
    ], 40, _hoisted_3), [
      [_vShow, !_unref(store).connection]
    ])
  ]))
}
}

})